import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Lottie from "lottie-react";
import checkAnimationData from "../animations/checkAnimationData";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

// 쿠키 설정 가져오기
const {
  KEYS: COOKIE_KEYS,
  EXPIRES: COOKIE_EXPIRES,
  OPTIONS: COOKIE_OPTIONS,
} = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 30,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    transition: {
      duration: 0.3,
    },
  },
};

// 웹 환경 감지 함수
const isWebEnvironment = () => {
  return window.innerWidth >= 768;
};

function CompletionPage() {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isWeb, setIsWeb] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    // 웹 환경 감지
    const webEnvironment = isWebEnvironment();
    setIsWeb(webEnvironment);

    // 화면 크기 변경 감지
    const handleResize = () => {
      setIsWeb(isWebEnvironment());
    };

    window.addEventListener("resize", handleResize);

    // 쿠키에서 USER_INFO 가져오기
    const userInfoFromCookie = Cookies.get("USER_INFO");
    if (userInfoFromCookie) {
      try {
        const parsedUserInfo = JSON.parse(userInfoFromCookie);
        setUserInfo(parsedUserInfo);
      } catch (error) {}
    }

    setIsLoading(false);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 1초 후 페이지 하단으로 스크롤
  useEffect(() => {
    const timer = setTimeout(() => {
      const scrollOptions = {
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      };

      window.scrollTo(scrollOptions);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // 완료 버튼 클릭 핸들러
  const handleComplete = (e) => {
    // 버튼 클릭 시 기본 동작 방지
    e.preventDefault();
    e.stopPropagation();

    // 네비게이션 상태 설정
    setIsNavigating(true);
    // 토큰 가져오기
    const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
    const refreshToken = Cookies.get(COOKIE_KEYS.REFRESH_TOKEN);

    // 모든 상태 초기화
    localStorage.clear(); // 로컬 스토리지 초기화
    sessionStorage.clear(); // 세션 스토리지 초기화

    // 쿠키 초기화
    Object.values(COOKIE_KEYS).forEach((key) => {
      Cookies.remove(key);
    });

    // 히스토리 스택을 리셋하면서 토큰과 함께 루트 경로로 이동
    window.location.replace(
      // `/?accessToken=${accessToken}&refreshToken=${refreshToken}`
      `/`
    );
  };

  // 사용자 정보 포맷
  const formatUserData = () => {
    // 기본 데이터 설정
    const defaultData = {
      dong: "000",
      ho: "0000",
      name: "000",
      apartment: "테스트 아파트 1단지",
      shareName: "000",
    };

    if (!userInfo) return defaultData;

    return {
      dong: userInfo.claimApartmentBlock || defaultData.dong,
      ho: userInfo.claimApartmentUnit || defaultData.ho,
      name: userInfo.claimAssignmentName || defaultData.name,
      shareName: userInfo.shareName || defaultData.shareName,
      apartment: userInfo.apartmentName || defaultData.apartment,
    };
  };

  const userData = formatUserData();

  // 로딩 중일 때만 로딩 화면 표시
  if (isLoading) {
    return (
      <motion.div
        className="page-container"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </motion.div>
    );
  }
  return (
    <div
      className="page-container"
      style={{
        overflow: "hidden",
        position: "relative",
        height: "100vh",
        width: "100%",
      }}
    >
      <div
        className="completion-content"
        style={
          isWeb
            ? {
                overflow: "auto",
                maxHeight: "calc(100vh - 150px)",
                width: "100%",
                maxWidth: "470px",
              }
            : {}
        }
      >
        <div className="animation-container">
          <Lottie
            animationData={checkAnimationData}
            loop={false}
            autoplay={true}
            style={{ width: 160, height: 160 }}
          />
        </div>
        {/* <img
          src="/completion.png"
          alt="completion"
          style={{ width: 73, height: 30, marginTop: -20, marginBottom: 20 }}
        /> */}

        <motion.div
          className="completion-message"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.5 }}
          style={{ textAlign: "center", alignSelf: "center", width: "100%" }}
        >
          <h2>공동소유 위임 동의 접수가</h2>
          <h3>완료되었습니다!</h3>
        </motion.div>

        <motion.div
          className="contract-summary"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          style={{
            backgroundColor: "#F8F9FA",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "20px",
          }}
        >
          <motion.h3
            style={{
              marginBottom: "20px",
              fontSize: "18px",
              fontWeight: "600",
              color: "#333",
              textAlign: "left",
            }}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
          >
            주요 내용 요약
          </motion.h3>
          <motion.div
            className="summary-row"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 1.0 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: "none",
            }}
          >
            <div
              className="summary-label"
              style={{ color: "#666", fontSize: "15px" }}
            >
              동·호수
            </div>
            <div
              className="summary-value"
              style={{ fontWeight: "600", color: "#333", fontSize: "15px" }}
            >
              {userData.dong}동 {userData.ho}호
            </div>
          </motion.div>
          <motion.div
            className="summary-row"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 1.1 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: "none",
            }}
          >
            <div
              className="summary-label"
              style={{ color: "#666", fontSize: "15px" }}
            >
              소유자 성명
            </div>
            <div
              className="summary-value"
              style={{ fontWeight: "600", color: "#333", fontSize: "15px" }}
            >
              {userData.name}
            </div>
          </motion.div>
          <motion.div
            className="summary-row"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 1.2 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: "none",
            }}
          >
            <div
              className="summary-label"
              style={{ color: "#666", fontSize: "15px" }}
            >
              위임자
            </div>
            <div
              className="summary-value"
              style={{ fontWeight: "600", color: "#333", fontSize: "15px" }}
            >
              {userData.shareName}
            </div>
          </motion.div>
          <motion.div
            className="summary-row"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 1.3 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: "none",
            }}
          >
            <div
              className="summary-label"
              style={{ color: "#666", fontSize: "15px" }}
            >
              수임자
            </div>
            <div
              className="summary-value"
              style={{ fontWeight: "600", color: "#333", fontSize: "15px" }}
            >
              {userData.apartment} 입주자대표회의
            </div>
          </motion.div>
          <motion.div
            className="summary-row"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 1.4 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: "none",
            }}
          >
            <div
              className="summary-label"
              style={{ color: "#666", fontSize: "15px" }}
            >
              위임 권한
            </div>
            <div
              className="summary-value"
              style={{ fontWeight: "600", color: "#333", fontSize: "15px" }}
            >
              의결권 행사
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* 하단 버튼 영역 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isNavigating ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
        transition={{
          delay: isNavigating ? 0 : 1.5,
          duration: isNavigating ? 0.1 : 0.6,
        }}
        style={{
          position: "fixed",
          bottom: "40px",
          left: "0",
          right: "0",
          padding: "20px 24px 40px",
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          paddingBottom: "env(safe-area-inset-bottom, 40px)",
        }}
      >
        {/* 완료 버튼 */}
        <button
          onClick={handleComplete}
          style={{
            width: "100%",
            height: "56px",
            backgroundColor: "#7B42F6",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "12px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "background-color 0.2s ease",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTapHighlightColor: "transparent",
            outline: "none",
            pointerEvents: "auto",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#6A35E5";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#7B42F6";
          }}
          onTouchStart={(e) => {
            e.target.style.backgroundColor = "#6A35E5";
          }}
          onTouchEnd={(e) => {
            e.target.style.backgroundColor = "#7B42F6";
          }}
        >
          완료
        </button>
      </motion.div>
    </div>
  );
}

export default CompletionPage;
